"use client";

import { useEffect, useState, useRef } from "react";
import { useScheduler } from "../SchedulerContext";
import { useTZDayjs } from "@/context/TimezoneContext";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { Button } from "@heroui/react";
import { callServerAction } from "@/util/functions/fetching/callServerAction";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { FaCalendarDay, FaLocationDot, FaScrewdriverWrench } from "react-icons/fa6";
import { parsePhoneNumber } from "libphonenumber-js";
import { useWhatConvertsEvents } from "../hooks/useWhatConvertsEvents";
import posthog from 'posthog-js';
import { EVENTS } from '@attik/posthog';

interface BookingJobLoaderProps {
  onComplete: (inspectionSlug: string) => void;
  onError: (error: string) => void;
  onRetry?: () => void;
}

interface InspectionResult {
  alternativeFunction: string;
  inspectionBody: any;
  inspection: any;
  assignResults: any;
  charges: any;
  chargeUpdates: any;
  eventResults: any;
}

const HOME_INSPECTION_PUNS = [
  "Climbing the ladders...",
  "Changing the flashlight batteries...",
  "Loading the tool belt...",
];

export default function BookingJobLoader({ onComplete, onError, onRetry }: BookingJobLoaderProps) {
  const {
    address,
    selectedDateTime,
    finalServices,
    selectedSlotInspectorInfo,
    selectedInspectorIdsForFilter,
    contact,
    selectedRole,
    additionalContacts,
    requiredInfoValues,
    equipmentQuantities,
    closingDate,
    priceData,
    scheduleSettings,
    draftQuoteId,
    jwtToken,
    companyId,
    appliedDiscountCode,
    appliedDiscountServiceIds,
    company,
    companyStyles,
    wcLeadId,
    workingHold,
    setBookingStatus,
    schedulerNotes,
  } = useScheduler();

  const tzDayjs = useTZDayjs();
  const { updateLead } = useWhatConvertsEvents();

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [currentPunIndex, setCurrentPunIndex] = useState(0);
  // FUTURE USE: Uncomment below for auto-redirect functionality
  // const [redirectCountdown, setRedirectCountdown] = useState(30);
  const hasBookedRef = useRef(false);

  // Rotate puns every 1 second
  useEffect(() => {
    if (status !== "loading") return;

    const interval = setInterval(() => {
      setCurrentPunIndex((prevIndex) => (prevIndex + 1) % HOME_INSPECTION_PUNS.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [status]);

  // FUTURE USE: Countdown timer for auto-redirect - Uncomment with redirectCountdown state above
  // useEffect(() => {
  //   if (status !== 'success') return;
  //
  //   const interval = setInterval(() => {
  //     setRedirectCountdown((prev) => {
  //       if (prev <= 1) {
  //         clearInterval(interval);
  //         return 0;
  //       }
  //       return prev - 1;
  //     });
  //   }, 1000);
  //
  //   return () => clearInterval(interval);
  // }, [status]);

  // Handle job booking - use ref to ensure it only runs once
  const bookJob = async () => {
    // Guard against double execution
    if (hasBookedRef.current) return;
    hasBookedRef.current = true;
    setBookingStatus("loading");

    try {
      // Validate required fields
      if (!address) throw new Error("Address is required");
      if (!selectedDateTime) throw new Error("Date/time is required");
      if (!finalServices || finalServices.length === 0)
        throw new Error("At least one service is required");
      if (!selectedSlotInspectorInfo) throw new Error("Inspector assignment is required");
      if (!contact || !selectedRole) throw new Error("Primary contact is required");

      // Transform contacts to API format
      const transformedContacts = [];

      // Primary contact
      transformedContacts.push({
        _roleId: selectedRole._id,
        _contactId: contact,
        primary: true,
      });

      // Additional contacts
      additionalContacts.forEach((ac) => {
        transformedContacts.push({
          _roleId: ac._roleId,
          _contactId: ac._contactId,
          primary: ac.primary || false,
        });
      });

      // Create job data
      const jobData = {
        address,
        datetime: selectedDateTime,
        finalServices,
        inspectorAssignment: [selectedSlotInspectorInfo._id], // Array of inspector IDs
        // Single preferred inspector → requested (auto-locks via schema hook)
        ...(selectedInspectorIdsForFilter?.length === 1 && {
          requested: selectedInspectorIdsForFilter,
        }),
        // Multiple preferred → lock whoever was assigned to the slot
        ...(selectedInspectorIdsForFilter?.length > 1 && {
          locked: [selectedSlotInspectorInfo._id],
        }),
        selectedContacts: transformedContacts,
        requiredInfoValues: requiredInfoValues || [],
        equipmentQuantities: equipmentQuantities || [],
        ...(closingDate && { closingDate }),
        totalDuration: priceData?.totalDuration,
        lockReport: true, // Default for online scheduler
        scheduledLocation: "online",
        ...(schedulerNotes.trim() && { inspectorNotes: schedulerNotes.trim() }),
        ...(appliedDiscountCode && {
          appliedDiscountCode,
          appliedDiscountServiceIds: appliedDiscountServiceIds || [],
        }),
        ...(wcLeadId && { wcLeadId }),
      };

      const useAttikBooking =
        company.attikStandalone === true ||
        (company.featureFlags?.includes("beta-online-scheduler-attik") ?? false);
      const endpoint = useAttikBooking ? "inspection/book-job" : "spectora/create-job";

      // Call create job API
      const result = await callServerAction<InspectionResult>({
        url: endpoint,
        method: "post",
        data: jobData,
        params: {
          _companyId: companyId,
        },
        headers: {
          "client-token": jwtToken,
        },
      });

      if (!result?.inspection?.data?.id) {
        throw new Error("Failed to create job - no inspection ID returned");
      }

      // Delete draft quote if it exists (no longer needed after job creation)
      if (draftQuoteId) {
        await callServerAction({
          url: `quote/${draftQuoteId}`,
          method: "delete",
          params: {
            _companyId: companyId,
          },
          headers: {
            "client-token": jwtToken,
          },
        }).catch((error) => {
          console.warn("Failed to delete draft quote:", error);
        });
      }

      // Success!
      setStatus("success");
      setBookingStatus("success");

      posthog.capture(EVENTS.schedulerJobBooked, {
        company_id: companyId,
        services: finalServices.map((s) => s.name),
        service_count: finalServices.length,
        total_price: priceData?.totalPrice,
        total_duration_hours: priceData?.totalDuration,
        discount_code: appliedDiscountCode?.code ?? null,
        scheduled_date: selectedDateTime,
      });

      // Update WhatConverts lead with quote and sales values (fire-and-forget)
      if (wcLeadId && priceData?.totalPrice) {
        updateLead(wcLeadId, {
          quoteValue: priceData.totalPrice,
          salesValue: priceData.totalPrice,
        }).catch((error) => {
          console.error("[BookingJobLoader] Failed to update WC lead:", error);
        });
      }

      // Delete working hold since job is now booked (fire-and-forget)
      if (workingHold) {
        callServerAction({
          url: `event/${workingHold._id}`,
          method: "delete",
          params: {
            _companyId: companyId,
          },
          headers: {
            "client-token": jwtToken,
          },
        }).catch((error) => {
          console.warn("[BookingJobLoader] Failed to delete working hold:", error);
        });
      }

      // Get slug for redirect
      const slug = result.inspection.data.attributes?.slug;
      if (!slug) {
        throw new Error("No inspection slug returned");
      }

      // FUTURE USE: Auto-redirect after 30 seconds - Uncomment with countdown timer above
      // setTimeout(() => {
      //   window.location.href = `${process.env.NEXT_PUBLIC_CLIENT_URL}/job/${slug}`;
      // }, 30000);

      onComplete(slug);
    } catch (error: any) {
      console.error("Error booking job:", error);
      setStatus("error");
      setBookingStatus("error");
      setErrorMessage(error.message || "Failed to create job");
      onError(error.message || "Failed to create job");

      posthog.capture(EVENTS.schedulerBookingFailed, {
        company_id: companyId,
        error_message: error.message || 'Unknown error',
        services: finalServices?.map((s) => s.name),
      });
    }
  };

  // Call booking function on mount, guarded by ref
  useEffect(() => {
    if (!hasBookedRef.current) {
      bookJob();
    }
  }, []);

  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    try {
      const phoneNumber = parsePhoneNumber(phone);
      return phoneNumber.formatNational(); // Returns format like (123) 456-7890
    } catch {
      return phone; // Fallback to original if parsing fails
    }
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col items-center justify-center py-8">
      {/* Loading State */}
      {status === "loading" && (
        <>
          <div style={{ width: "600px", height: "375px" }}>
            <DotLottieReact
              src="https://lottie.host/4f8822fd-2c97-458a-b375-ea12dc356d84/CDAl7cfiK7.lottie"
              loop
              speed={0.3}
              autoplay
            />
          </div>
          <div className="mt-4 flex h-12 items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.p
                key={currentPunIndex}
                initial={{ rotateX: 90, opacity: 0 }}
                animate={{ rotateX: 0, opacity: 1 }}
                exit={{ rotateX: -90, opacity: 0 }}
                transition={{
                  duration: 0.5,
                  ease: "easeInOut",
                }}
                className="text-xl font-medium text-gray-700"
              >
                {HOME_INSPECTION_PUNS[currentPunIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Success State */}
      {status === "success" && (
        <div className="text-center">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              duration: 0.5,
            }}
            className="mb-6 flex justify-center"
          >
            <FaCheckCircle className="text-success" size={120} />
          </motion.div>
          <h2 className="mb-2 text-2xl font-bold text-gray-800">Job Successfully Scheduled!</h2>
          <p className="mb-6 text-gray-600">
            Thanks for your business! We will be in touch to confirm any needed details.
          </p>

          {/* Booking Recap */}
          <div className="mx-auto mb-6 max-w-md rounded-lg border border-gray-200 bg-white p-4 text-left">
            {/* Date & Time */}
            {selectedDateTime && (
              <div className="flex items-start gap-3 border-b border-gray-100 pb-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: companyStyles.companyColor
                      ? `${companyStyles.companyColor}1A`
                      : "rgba(0, 111, 238, 0.1)",
                    color: companyStyles.companyColor || "#006FEE",
                  }}
                >
                  <FaCalendarDay size={14} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {tzDayjs(selectedDateTime).format("dddd, MMMM D, YYYY")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {tzDayjs(selectedDateTime).format("h:mm A")}
                    {scheduleSettings?.showDurationInOnlineScheduler &&
                      priceData?.totalDuration && (
                        <span className="text-gray-400">
                          {" "}
                          (~{Math.floor(priceData.totalDuration)}-
                          {Math.ceil(priceData.totalDuration)} hrs)
                        </span>
                      )}
                  </p>
                </div>
              </div>
            )}

            {/* Address */}
            {address && (
              <div className="flex items-start gap-3 border-b border-gray-100 py-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: companyStyles.companyColor
                      ? `${companyStyles.companyColor}1A`
                      : "rgba(0, 111, 238, 0.1)",
                    color: companyStyles.companyColor || "#006FEE",
                  }}
                >
                  <FaLocationDot size={14} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {address.street}
                    {address.unit && `, ${address.unit}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    {address.city}, {address.state} {address.zip}
                  </p>
                </div>
              </div>
            )}

            {/* Services */}
            {finalServices && finalServices.length > 0 && (
              <div className="flex items-start gap-3 pt-3">
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                  style={{
                    backgroundColor: companyStyles.companyColor
                      ? `${companyStyles.companyColor}1A`
                      : "rgba(0, 111, 238, 0.1)",
                    color: companyStyles.companyColor || "#006FEE",
                  }}
                >
                  <FaScrewdriverWrench size={14} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">
                    {finalServices.length} Service
                    {finalServices.length !== 1 ? "s" : ""}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {finalServices.map((service) => (
                      <li key={service._id} className="text-sm text-gray-600">
                        {service.name}
                      </li>
                    ))}
                  </ul>
                  {priceData?.totalPrice !== undefined && priceData.totalPrice > 0 && (
                    <p className="mt-2 text-sm font-semibold text-gray-800">
                      Total: ${priceData.totalPrice.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Company Contact Information */}
          {company && (
            <div className="mx-auto mt-8 max-w-md rounded-lg border border-gray-200 bg-gray-50 p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-800">Need to reach us?</h3>
              <div className="space-y-2 text-gray-700">
                {company.name && <p className="font-medium">{company.name}</p>}
                {company.phone && (
                  <p>
                    <span className="font-medium">Phone:</span>{" "}
                    <a
                      href={`tel:${company.phone.replace(/^\+/, "")}`}
                      className="hover:underline"
                      style={{ color: companyStyles.companyColor || "#2563eb" }}
                    >
                      {formatPhoneNumber(company.phone)}
                    </a>
                  </p>
                )}
                {company.email && (
                  <p>
                    <span className="font-medium">Email:</span>{" "}
                    <a
                      href={`mailto:${company.email}`}
                      className="hover:underline"
                      style={{ color: companyStyles.companyColor || "#2563eb" }}
                    >
                      {company.email}
                    </a>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* FUTURE USE: Countdown text for auto-redirect - Uncomment with countdown timer above
          <p className='text-gray-600'>
            Redirecting to your job portal in {redirectCountdown} second
            {redirectCountdown !== 1 ? 's' : ''}...
          </p>
          */}
        </div>
      )}

      {/* Error State */}
      {status === "error" && (
        <div className="text-center">
          <div style={{ maxWidth: "300px", margin: "0 auto" }}>
            <DotLottieReact
              src="https://lottie.host/63049464-e5ed-4a06-a1d6-d26f0c21859b/UoohR1Pp6C.lottie"
              speed={0.5}
              autoplay
            />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-800">Error Creating Job</h2>
          <p className="mb-4 text-gray-600">{errorMessage}</p>
          {onRetry && (
            <Button color="danger" onPress={onRetry} className="font-bold uppercase">
              Try Again
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
